/**
 * Stellar StrKey encoding implementation
 * Based on the Stellar library's strkey encoding https://github.com/stellar/js-stellar-base/blob/b82822b3b152453c57d52fc988375b509a113433/src/strkey.js
 */

// Version bytes for different Stellar key types
const versionBytes = {
	ed25519PublicKey: 6 << 3, // G (when encoded in base32)
	ed25519SecretSeed: 18 << 3, // S
} as const;

// StrKey type mapping
const strkeyTypes = {
	G: "ed25519PublicKey",
	S: "ed25519SecretSeed",
} as const;

export type StrKeyType = keyof typeof strkeyTypes;
export type VersionByteName = keyof typeof versionBytes;

/**
 * Calculate CRC16-XModem checksum for Stellar StrKey
 * @param data - Data to calculate checksum for
 * @returns 2-byte checksum in little-endian order
 */
function calculateChecksum(data: Uint8Array): Uint8Array {
	// CRC16-XModem lookup table from Stellar's implementation
	const crcTable = [
		0x0000, 0x1021, 0x2042, 0x3063, 0x4084, 0x50a5, 0x60c6, 0x70e7, 0x8108,
		0x9129, 0xa14a, 0xb16b, 0xc18c, 0xd1ad, 0xe1ce, 0xf1ef, 0x1231, 0x0210,
		0x3273, 0x2252, 0x52b5, 0x4294, 0x72f7, 0x62d6, 0x9339, 0x8318, 0xb37b,
		0xa35a, 0xd3bd, 0xc39c, 0xf3ff, 0xe3de, 0x2462, 0x3443, 0x0420, 0x1401,
		0x64e6, 0x74c7, 0x44a4, 0x5485, 0xa56a, 0xb54b, 0x8528, 0x9509, 0xe5ee,
		0xf5cf, 0xc5ac, 0xd58d, 0x3653, 0x2672, 0x1611, 0x0630, 0x76d7, 0x66f6,
		0x5695, 0x46b4, 0xb75b, 0xa77a, 0x9719, 0x8738, 0xf7df, 0xe7fe, 0xd79d,
		0xc7bc, 0x48c4, 0x58e5, 0x6886, 0x78a7, 0x0840, 0x1861, 0x2802, 0x3823,
		0xc9cc, 0xd9ed, 0xe98e, 0xf9af, 0x8948, 0x9969, 0xa90a, 0xb92b, 0x5af5,
		0x4ad4, 0x7ab7, 0x6a96, 0x1a71, 0x0a50, 0x3a33, 0x2a12, 0xdbfd, 0xcbdc,
		0xfbbf, 0xeb9e, 0x9b79, 0x8b58, 0xbb3b, 0xab1a, 0x6ca6, 0x7c87, 0x4ce4,
		0x5cc5, 0x2c22, 0x3c03, 0x0c60, 0x1c41, 0xedae, 0xfd8f, 0xcdec, 0xddcd,
		0xad2a, 0xbd0b, 0x8d68, 0x9d49, 0x7e97, 0x6eb6, 0x5ed5, 0x4ef4, 0x3e13,
		0x2e32, 0x1e51, 0x0e70, 0xff9f, 0xefbe, 0xdfdd, 0xcffc, 0xbf1b, 0xaf3a,
		0x9f59, 0x8f78, 0x9188, 0x81a9, 0xb1ca, 0xa1eb, 0xd10c, 0xc12d, 0xf14e,
		0xe16f, 0x1080, 0x00a1, 0x30c2, 0x20e3, 0x5004, 0x4025, 0x7046, 0x6067,
		0x83b9, 0x9398, 0xa3fb, 0xb3da, 0xc33d, 0xd31c, 0xe37f, 0xf35e, 0x02b1,
		0x1290, 0x22f3, 0x32d2, 0x4235, 0x5214, 0x6277, 0x7256, 0xb5ea, 0xa5cb,
		0x95a8, 0x8589, 0xf56e, 0xe54f, 0xd52c, 0xc50d, 0x34e2, 0x24c3, 0x14a0,
		0x0481, 0x7466, 0x6447, 0x5424, 0x4405, 0xa7db, 0xb7fa, 0x8799, 0x97b8,
		0xe75f, 0xf77e, 0xc71d, 0xd73c, 0x26d3, 0x36f2, 0x0691, 0x16b0, 0x6657,
		0x7676, 0x4615, 0x5634, 0xd94c, 0xc96d, 0xf90e, 0xe92f, 0x99c8, 0x89e9,
		0xb98a, 0xa9ab, 0x5844, 0x4865, 0x7806, 0x6827, 0x18c0, 0x08e1, 0x3882,
		0x28a3, 0xcb7d, 0xdb5c, 0xeb3f, 0xfb1e, 0x8bf9, 0x9bd8, 0xabbb, 0xbb9a,
		0x4a75, 0x5a54, 0x6a37, 0x7a16, 0x0af1, 0x1ad0, 0x2ab3, 0x3a92, 0xfd2e,
		0xed0f, 0xdd6c, 0xcd4d, 0xbdaa, 0xad8b, 0x9de8, 0x8dc9, 0x7c26, 0x6c07,
		0x5c64, 0x4c45, 0x3ca2, 0x2c83, 0x1ce0, 0x0cc1, 0xef1f, 0xff3e, 0xcf5d,
		0xdf7c, 0xaf9b, 0xbfba, 0x8fd9, 0x9ff8, 0x6e17, 0x7e36, 0x4e55, 0x5e74,
		0x2e93, 0x3eb2, 0x0ed1, 0x1ef0,
	];

	let crc16 = 0x0;

	for (let i = 0; i < data.length; i += 1) {
		const byte = data[i];
		if (byte == null) {
			throw new Error(`Invalid data: undefined byte at index ${i}`);
		}
		const lookupIndex = (crc16 >> 8) ^ byte;
		const tableValue = crcTable[lookupIndex];
		if (tableValue == null) {
			throw new Error(`Invalid lookup index: ${lookupIndex}`);
		}
		crc16 = (crc16 << 8) ^ tableValue;
		crc16 &= 0xffff;
	}

	// Return in little-endian order as per Stellar's implementation
	const checksum = new Uint8Array(2);
	checksum[0] = crc16 & 0xff;
	checksum[1] = (crc16 >> 8) & 0xff;
	return checksum;
}

/**
 * Encode data with version byte and checksum
 * @param versionByteName - Name of the version byte to use
 * @param data - Data to encode
 * @returns Base32 encoded string
 */
function encodeCheck(
	versionByteName: VersionByteName,
	data: Uint8Array,
): string {
	if (!data || data.length === 0) {
		throw new Error("cannot encode null or empty data");
	}

	const versionByte = versionBytes[versionByteName];
	if (versionByte === undefined) {
		throw new Error(
			`${versionByteName} is not a valid version byte name. ` +
				`Expected one of ${Object.keys(versionBytes).join(", ")}`,
		);
	}

	const versionBuffer = new Uint8Array([versionByte]);
	const payload = new Uint8Array(versionBuffer.length + data.length);
	payload.set(versionBuffer, 0);
	payload.set(data, versionBuffer.length);

	const checksum = calculateChecksum(payload);
	const unencoded = new Uint8Array(payload.length + checksum.length);
	unencoded.set(payload, 0);
	unencoded.set(checksum, payload.length);

	return base32Encode(unencoded);
}

/**
 * Base32 encoding implementation matching Stellar's behavior
 * @param data - Data to encode
 * @returns Base32 encoded string
 */
function base32Encode(data: Uint8Array): string {
	const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
	let bits = 0;
	let value = 0;
	let output = "";

	for (let i = 0; i < data.length; i++) {
		const byte = data[i];
		if (byte == null) {
			throw new Error(`Invalid data: undefined byte at index ${i}`);
		}
		value = (value << 8) | byte;
		bits += 8;

		while (bits >= 5) {
			output += alphabet[(value >>> (bits - 5)) & 31];
			bits -= 5;
		}
	}

	if (bits > 0) {
		output += alphabet[(value << (5 - bits)) & 31];
	}

	// Add padding to make length divisible by 8
	while (output.length % 8 !== 0) {
		output += "=";
	}

	return output;
}

/**
 * Decode base32 string to bytes
 * @param encoded - Base32 encoded string
 * @returns Decoded bytes
 */
function base32Decode(encoded: string): Uint8Array {
	const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
	const padding = "=";

	// Remove padding and convert to uppercase
	encoded = encoded.replace(/=+$/, "").toUpperCase();

	let bits = 0;
	let value = 0;
	const output: number[] = [];

	for (let i = 0; i < encoded.length; i++) {
		const char = encoded[i];
		if (char === undefined) {
			throw new Error(
				`Invalid encoded string: undefined character at index ${i}`,
			);
		}
		if (char === padding) break;

		const index = alphabet.indexOf(char);
		if (index === -1) {
			throw new Error(`Invalid base32 character: ${char}`);
		}

		value = (value << 5) | index;
		bits += 5;

		if (bits >= 8) {
			output.push((value >>> (bits - 8)) & 255);
			bits -= 8;
		}
	}

	return new Uint8Array(output);
}

/**
 * Decode StrKey and extract version byte and data
 * @param encoded - StrKey encoded string
 * @returns Object containing version byte name and data
 */
function decodeCheck(encoded: string): {
	versionByteName: VersionByteName;
	data: Uint8Array;
} {
	try {
		const decoded = base32Decode(encoded);

		if (decoded.length < 3) {
			throw new Error("StrKey too short");
		}

		const versionByte = decoded[0];
		const data = decoded.slice(1, -2);
		const checksum = decoded.slice(-2);

		// Find version byte name
		const versionByteName = Object.entries(versionBytes).find(
			([_, value]) => value === versionByte,
		)?.[0] as VersionByteName;

		if (!versionByteName) {
			throw new Error(`Unknown version byte: ${versionByte}`);
		}

		// Verify checksum
		const payload = new Uint8Array(decoded.length - 2);
		payload.set(decoded.slice(0, -2));
		const expectedChecksum = calculateChecksum(payload);

		if (!checksum.every((byte, i) => byte === expectedChecksum[i])) {
			throw new Error("Invalid checksum");
		}

		return { versionByteName, data };
	} catch (error) {
		throw new Error(
			`Failed to decode StrKey: ${error instanceof Error ? error.message : "Unknown error"}`,
		);
	}
}

/**
 * Encode Ed25519 public key to StrKey
 * @param publicKey - Ed25519 public key bytes
 * @returns StrKey encoded string
 */
export function encodeEd25519PublicKey(publicKey: Uint8Array): string {
	return encodeCheck("ed25519PublicKey", publicKey);
}

/**
 * Encode Ed25519 secret seed to StrKey
 * @param secretSeed - Ed25519 secret seed bytes
 * @returns StrKey encoded string
 */
export function encodeEd25519SecretSeed(secretSeed: Uint8Array): string {
	return encodeCheck("ed25519SecretSeed", secretSeed);
}

/**
 * Decode StrKey to extract data
 * @param encoded - StrKey encoded string
 * @returns Object containing version byte name and data
 */
export function decodeStrKey(encoded: string): {
	versionByteName: VersionByteName;
	data: Uint8Array;
} {
	return decodeCheck(encoded);
}

/**
 * Check if a string is a valid StrKey
 * @param str - String to validate
 * @returns True if valid StrKey, false otherwise
 */
export function isValidStrKey(str: string): boolean {
	try {
		decodeCheck(str);
		return true;
	} catch {
		return false;
	}
}

/**
 * Get the type of StrKey from the encoded string
 * @param encoded - StrKey encoded string
 * @returns StrKey type character (G, S, M, T, X, P, C, L, B)
 */
export function getStrKeyType(encoded: string): StrKeyType {
	const { versionByteName } = decodeCheck(encoded);
	const typeChar = Object.entries(strkeyTypes).find(
		([_, value]) => value === versionByteName,
	)?.[0] as StrKeyType;

	if (!typeChar) {
		throw new Error(`Unknown StrKey type for version: ${versionByteName}`);
	}

	return typeChar;
}
